import { useRef, type KeyboardEvent } from 'react';
import type { BrandImportJobView, BrandOnboardingStartMode } from '@mg/shared';
import {
  EDIT_MANUALLY_CHOICE,
  MANUAL_SUGGESTIONS_NOTICE,
  REREAD_CHOICE,
  START_CHOICE_COPY,
} from '../../brand/brandCopy';
import { REREAD_ENABLED } from '../../brand/flags/reread';
import { SUGGESTIONS_ENABLED } from '../../brand/flags/suggestions';
import { useBrandDraft } from '../draft/BrandDraftContext';
import { useRevisit } from '../revisit/RevisitProvider';
import type { WizardMode } from '../steps';
import { REVISIT_SOURCES_COPY } from '../wizardCopy';
import { DocumentBox } from './DocumentBox';
import { RevisitDocumentBox, RevisitPageList } from './RevisitSourceBoxes';
import { UrlListBox } from './UrlListBox';

/** Revisit's choice once re-reading exists (Release 4). */
export type RevisitChoice = 'reread' | 'manual';

interface RadioOption<V extends string> {
  value: V;
  label: string;
  description: string;
}

const CARD =
  'focus-ring flex flex-col items-start gap-1 rounded-xl border border-line bg-card p-4 text-left disabled:cursor-not-allowed aria-checked:border-2 aria-checked:border-action aria-checked:bg-tint aria-checked:p-[calc(1rem-1px)]';

/**
 * Radio cards with roving focus (the AppearanceCard pattern): Tab enters the group once, at the chosen
 * card, and the arrow keys move the choice and the focus, wrapping.
 */
function RadioCards<V extends string>({
  labelledBy,
  options,
  value,
  onChange,
  disabled,
}: {
  labelledBy: string;
  options: readonly RadioOption<V>[];
  value: V;
  onChange: (next: V) => void;
  disabled: boolean;
}) {
  const cards = useRef(new Map<V, HTMLButtonElement>());
  const move = (from: number, delta: number) => {
    const next = options[(from + delta + options.length) % options.length];
    onChange(next.value);
    cards.current.get(next.value)?.focus();
  };
  const onKeyDown = (index: number) => (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      move(index, 1);
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();
      move(index, -1);
    }
  };
  return (
    <div role="radiogroup" aria-labelledby={labelledBy} className="grid gap-3 md:grid-cols-2">
      {options.map((option, index) => {
        const checked = option.value === value;
        return (
          <button
            key={option.value}
            ref={(el) => {
              if (el) cards.current.set(option.value, el);
              else cards.current.delete(option.value);
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={checked ? 0 : -1}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            onKeyDown={onKeyDown(index)}
            className={CARD}
          >
            <span className="text-sm font-semibold text-ink">{option.label}</span>
            <span className="text-sm text-ink-muted">{option.description}</span>
          </button>
        );
      })}
    </div>
  );
}

const START_OPTIONS: readonly RadioOption<BrandOnboardingStartMode>[] = [
  { value: 'UPLOAD', ...START_CHOICE_COPY.upload },
  { value: 'MANUAL', ...START_CHOICE_COPY.manual },
];

const REVISIT_OPTIONS: readonly RadioOption<RevisitChoice>[] = [
  { value: 'reread', ...REREAD_CHOICE },
  { value: 'manual', ...EDIT_MANUALLY_CHOICE },
];

/** What the wizard knows of a revisit's sources (useWizardController). */
export interface RevisitSourcesState {
  /** The workspace's latest import of either kind: its document and web pages are what a re-read reads. */
  job: BrandImportJobView | null;
  /** The latest import is not read yet (or could not be): the boxes wait, so the page rows start from it. */
  loading: boolean;
  /** The latest import could not be read: the boxes stay locked, and say so. */
  loadFailed: boolean;
  removedDocument: boolean;
  onRemoveDocument: () => void;
  onKeepDocument: () => void;
  /** The address typed in the web-page box, not added yet. */
  pageDraft: string;
  onPageDraftChange: (text: string) => void;
  /** Failed pages the person asked to read again (Try again). */
  retryingPages: readonly string[];
  onRetryPage: (url: string) => void;
}

/**
 * Revisit with Re-read my sources (Release 4; the "Onboarding wizard (revisit)" board): the document and
 * the web pages as rows with how each was read, so the person can replace or remove the document, add or
 * remove pages and try a failed page again before Next re-reads them. While a re-read is open (running,
 * or its changes under review) its sources stay as they were: Next keeps that re-read rather than
 * starting another.
 */
function RevisitSources({
  job,
  loading,
  loadFailed,
  removedDocument,
  onRemoveDocument,
  onKeepDocument,
  pageDraft,
  onPageDraftChange,
  retryingPages,
  onRetryPage,
  disabled,
  pendingFile,
  fileError,
  uploading,
  uploadProgress,
  urls,
  onUrlsChange,
  onChooseFile,
  onRemoveFile,
}: RevisitSourcesState & {
  disabled: boolean;
  pendingFile: File | null;
  fileError: string | null;
  uploading: boolean;
  uploadProgress: number | null;
  urls: string[];
  onUrlsChange: (urls: string[]) => void;
  onChooseFile: (file: File) => void;
  onRemoveFile: () => void;
}) {
  const run = useRevisit()?.run.kind;
  const held = run === 'starting' || run === 'running' || run === 'review';
  const locked = disabled || loading || held;
  return (
    <>
      {held && <p className="text-sm text-ink-muted">{REVISIT_SOURCES_COPY.held}</p>}
      {!held && loadFailed && (
        <p role="alert" className="text-sm text-red-600 dark:text-error-dark">
          {REVISIT_SOURCES_COPY.loadFailed}
        </p>
      )}
      <RevisitDocumentBox
        current={job?.document ?? null}
        removed={removedDocument}
        pendingFile={pendingFile}
        error={fileError}
        uploading={uploading}
        uploadProgress={uploadProgress}
        disabled={locked}
        onChoose={onChooseFile}
        onRemovePending={onRemoveFile}
        onRemoveCurrent={onRemoveDocument}
        onUndoRemove={onKeepDocument}
      />
      <RevisitPageList
        urls={urls}
        onChange={onUrlsChange}
        draft={pageDraft}
        onDraftChange={onPageDraftChange}
        stored={job?.pages ?? []}
        retrying={retryingPages}
        onRetry={onRetryPage}
        disabled={locked}
      />
    </>
  );
}

/**
 * Step 1 (spec:85-90; web.md §5.1).
 * - Setup: two radio cards, labelled by the step's title. Upload and prefill (the default) shows the
 *   document box (and, in Release 4, the web-page box); Fill in manually clears what an import filled
 *   in, never what was saved.
 * - Revisit: in Release 3 one static card, Edit manually: nothing changes until a field is edited and
 *   its step saved. Release 4 adds Re-read my sources as a second choice, with the document and web-page
 *   boxes under it (RevisitSources).
 */
export function StartStep({
  mode,
  titleId,
  disabled,
  pendingFile,
  fileError,
  uploading,
  uploadProgress,
  currentDocument,
  pages,
  urls,
  onUrlsChange,
  onChooseFile,
  onRemoveFile,
  revisitChoice,
  onRevisitChoice,
  revisitSources,
}: {
  mode: WizardMode;
  /** The step title's id: the radio group's label. */
  titleId: string;
  disabled: boolean;
  pendingFile: File | null;
  fileError: string | null;
  uploading: boolean;
  uploadProgress: number | null;
  currentDocument: BrandImportJobView['document'];
  pages: BrandImportJobView['pages'] | undefined;
  urls: string[];
  onUrlsChange: (urls: string[]) => void;
  onChooseFile: (file: File) => void;
  onRemoveFile: () => void;
  revisitChoice: RevisitChoice;
  onRevisitChoice: (choice: RevisitChoice) => void;
  /** Revisit: the sources Re-read my sources reads, to change before Next. */
  revisitSources?: RevisitSourcesState;
}) {
  const { state, dispatch } = useBrandDraft();

  if (mode === 'revisit') {
    if (REREAD_ENABLED) {
      return (
        <>
          <RadioCards
            labelledBy={titleId}
            options={REVISIT_OPTIONS}
            value={revisitChoice}
            onChange={onRevisitChoice}
            disabled={disabled}
          />
          {revisitChoice === 'reread' && revisitSources && (
            <RevisitSources
              {...revisitSources}
              disabled={disabled}
              pendingFile={pendingFile}
              fileError={fileError}
              uploading={uploading}
              uploadProgress={uploadProgress}
              urls={urls}
              onUrlsChange={onUrlsChange}
              onChooseFile={onChooseFile}
              onRemoveFile={onRemoveFile}
            />
          )}
        </>
      );
    }
    return (
      <div className="rounded-xl border-2 border-action bg-tint p-[calc(1rem-1px)]">
        <p className="text-sm font-semibold text-ink">{EDIT_MANUALLY_CHOICE.label}</p>
        <p className="mt-1 text-sm text-ink-muted">{EDIT_MANUALLY_CHOICE.description}</p>
      </div>
    );
  }

  return (
    <>
      <RadioCards
        labelledBy={titleId}
        options={START_OPTIONS}
        value={state.startMode}
        onChange={(next) => {
          if (next !== state.startMode) dispatch({ type: 'setStartMode', mode: next });
        }}
        disabled={disabled}
      />
      {state.startMode === 'UPLOAD' && (
        <>
          <DocumentBox
            pendingFile={pendingFile}
            current={currentDocument ? { fileName: currentDocument.fileName, uploadedAt: currentDocument.uploadedAt } : null}
            error={fileError}
            uploading={uploading}
            uploadProgress={uploadProgress}
            disabled={disabled}
            onChoose={onChooseFile}
            onRemove={onRemoveFile}
          />
          <UrlListBox urls={urls} onChange={onUrlsChange} disabled={disabled} previous={pages} />
        </>
      )}
      {state.startMode === 'MANUAL' && SUGGESTIONS_ENABLED && (
        <p className="text-sm text-ink-muted">{MANUAL_SUGGESTIONS_NOTICE}</p>
      )}
    </>
  );
}
